(() => {
  'use strict';

  if (window.HousiousQuickAddPhase1) return;
  window.HousiousQuickAddPhase1 = true;

  const productCache = new Map();
  let overlay = null;
  let modal = null;
  let lastTrigger = null;
  let previousBodyOverflow = '';
  let product = null;
  let currentVariant = null;
  let selectedOptions = [];
  let activeImageIndex = 0;
  let quantity = 1;
  let scrollTimer = 0;

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const esc = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalizeUrl = (value) => {
    if (!value) return '';
    const raw = typeof value === 'string' ? value : (value.src || value.url || '');
    return String(raw).split('?')[0].split('#')[0];
  };

  const imageUrl = (value) => {
    if (!value) return '';
    return typeof value === 'string' ? value : (value.src || value.url || '');
  };

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
    return `₹${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const formatRupees = (rupees) => `₹${new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(Math.max(0, Math.round(Number(rupees || 0))))}`;

  function productOptions(data) {
    const variants = Array.isArray(data?.variants) ? data.variants : [];
    return (Array.isArray(data?.options) ? data.options : []).map((option, index) => {
      if (typeof option === 'string') {
        return {
          name: option,
          values: [...new Set(variants.map((variant) => variant?.options?.[index]).filter(Boolean))]
        };
      }
      return {
        name: option?.name || `Option ${index + 1}`,
        values: Array.isArray(option?.values) && option.values.length
          ? option.values
          : [...new Set(variants.map((variant) => variant?.options?.[index]).filter(Boolean))]
      };
    });
  }

  function firstAvailableVariant(data) {
    const variants = Array.isArray(data?.variants) ? data.variants : [];
    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function findVariant() {
    if (!product || !Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => {
      const options = Array.isArray(variant.options) ? variant.options : [];
      return selectedOptions.every((value, index) => options[index] === value);
    }) || null;
  }

  function imageForOptionValue(optionIndex, value) {
    if (!product?.variants) return '';
    const variant = product.variants.find((item) => item?.options?.[optionIndex] === value && item?.featured_image);
    return imageUrl(variant?.featured_image);
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load this product.');
    const data = await response.json();
    productCache.set(handle, data);
    return data;
  }

  function ensureModal() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'quick-add-modal-overlay housious-quick-add-v1';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = '<div class="quick-add-modal" role="dialog" aria-modal="true" aria-label="Quick add product"><div class="quick-add-modal-shell"><div class="quick-add-modal-loading" role="status">Loading product…</div></div></div>';
    document.body.appendChild(overlay);
    modal = qs('.quick-add-modal', overlay);

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target.closest('[data-quick-add-close]')) close();
    });

    modal.addEventListener('click', onModalClick);
    modal.addEventListener('input', onModalInput);
    modal.addEventListener('change', onModalInput);
    modal.addEventListener('scroll', () => {}, { passive: true });
  }

  async function open(handle, productUrl, trigger) {
    if (!handle) return;
    ensureModal();
    lastTrigger = trigger || null;
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    modal.innerHTML = '<div class="quick-add-modal-shell"><button class="quick-add-modal-close" type="button" data-quick-add-close aria-label="Close quick add">×</button><div class="quick-add-modal-loading" role="status"><span class="quick-add-modal-spinner" aria-hidden="true"></span>Loading product…</div></div>';

    try {
      product = await fetchProduct(handle);
      currentVariant = firstAvailableVariant(product);
      selectedOptions = Array.isArray(currentVariant?.options) ? [...currentVariant.options] : [];
      activeImageIndex = 0;
      quantity = 1;
      render(productUrl || product.url || `/products/${handle}`);
      requestAnimationFrame(() => qs('.quick-add-modal-close', modal)?.focus({ preventScroll: true }));
    } catch (error) {
      modal.innerHTML = `<div class="quick-add-modal-shell"><button class="quick-add-modal-close" type="button" data-quick-add-close aria-label="Close quick add">×</button><div class="quick-add-modal-error" role="alert">${esc(error?.message || 'Unable to load this product.')}</div></div>`;
    }
  }

  function render(productUrl) {
    const images = (Array.isArray(product?.images) ? product.images : []).map(imageUrl).filter(Boolean);
    const fallbackImage = imageUrl(currentVariant?.featured_image);
    if (!images.length && fallbackImage) images.push(fallbackImage);

    if (currentVariant?.featured_image && images.length) {
      const wanted = normalizeUrl(currentVariant.featured_image);
      const index = images.findIndex((img) => normalizeUrl(img) === wanted);
      if (index >= 0) activeImageIndex = index;
    }

    const options = productOptions(product);
    const galleryHtml = images.length
      ? images.map((src, index) => `<div class="quick-add-media-slide" data-image-index="${index}"><img src="${esc(src)}" alt="${esc(product.title)}" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async"></div>`).join('')
      : `<div class="quick-add-media-slide quick-add-media-slide--empty"><span>No image</span></div>`;

    const thumbsHtml = images.map((src, index) => `<button class="quick-add-modal-thumb${index === activeImageIndex ? ' is-active' : ''}" type="button" data-image-index="${index}" aria-label="View image ${index + 1}" aria-current="${index === activeImageIndex ? 'true' : 'false'}"><img src="${esc(src)}" alt="" loading="lazy" decoding="async"></button>`).join('');

    const optionsHtml = options.map((option, optionIndex) => {
      const values = option.values || [];
      return `<div class="quick-add-option-group" data-option-index="${optionIndex}">
        <div class="quick-add-option-label">${esc(option.name)}: <span data-selected-option-label>${esc(selectedOptions[optionIndex] || '')}</span></div>
        <div class="quick-add-option-values" role="listbox" aria-label="${esc(option.name)}">
          ${values.map((value) => {
            const swatchImage = imageForOptionValue(optionIndex, value);
            const selected = selectedOptions[optionIndex] === value;
            return `<button class="quick-add-swatch-card${selected ? ' is-selected' : ''}${swatchImage ? '' : ' quick-add-swatch-card--text-only'}" type="button" data-option-index="${optionIndex}" data-option-value="${esc(value)}" role="option" aria-selected="${selected ? 'true' : 'false'}">
              ${swatchImage ? `<span class="quick-add-swatch-card__image"><img src="${esc(swatchImage)}" alt="" loading="lazy" decoding="async"></span>` : ''}
              <span class="quick-add-swatch-card__text">${esc(value)}</span>
            </button>`;
          }).join('')}
        </div>
      </div>`;
    }).join('');

    modal.innerHTML = `<div class="quick-add-modal-shell">
      <button class="quick-add-modal-close" type="button" data-quick-add-close aria-label="Close quick add">×</button>
      <div class="quick-add-modal-body">
        <section class="quick-add-modal-media" aria-label="Product images">
          <div class="quick-add-media-stage">
            <div class="quick-add-media-track">${galleryHtml}</div>
            ${images.length > 1 ? '<button class="quick-add-gallery-arrow quick-add-gallery-arrow--prev" type="button" data-gallery-prev aria-label="Previous image">‹</button><button class="quick-add-gallery-arrow quick-add-gallery-arrow--next" type="button" data-gallery-next aria-label="Next image">›</button>' : ''}
            <div class="quick-add-image-counter" aria-live="polite"><span data-image-current>${images.length ? activeImageIndex + 1 : 0}</span>/<span data-image-total>${images.length}</span></div>
          </div>
          ${images.length > 1 ? `<div class="quick-add-modal-thumbs">${thumbsHtml}</div>` : ''}
        </section>

        <section class="quick-add-modal-info">
          <h2 class="quick-add-modal-title">${esc(product.title)}</h2>

          <div class="quick-add-price-card" aria-live="polite">
            <div class="quick-add-modal-price">
              <span class="quick-add-modal-price-current" data-price-current></span>
              <span class="quick-add-modal-price-compare" data-price-compare hidden></span>
              <span class="quick-add-price-discount" data-price-discount hidden></span>
            </div>
            <div class="quick-add-tax-note">Inclusive of all taxes</div>
          </div>

          <div class="quick-add-offers-grid" aria-label="Payment offers">
            <div class="quick-add-offer-card quick-add-offer-card--prepaid">
              <div class="quick-add-offer-card__eyebrow">Prepaid Price <span>10% OFF</span></div>
              <div class="quick-add-offer-card__row"><strong data-prepaid-price></strong><small data-prepaid-save></small></div>
            </div>
            <div class="quick-add-offer-card quick-add-offer-card--upi">
              <div class="quick-add-offer-card__eyebrow">UPI Special Price <span class="quick-add-upi-pill">UPI</span></div>
              <div class="quick-add-offer-card__row"><strong data-upi-price></strong><small data-upi-save></small></div>
            </div>
          </div>

          ${optionsHtml ? `<div class="quick-add-options-wrap">${optionsHtml}</div>` : ''}

          <div class="quick-add-purchase-card">
            <div class="quick-add-purchase-topline">
              <span class="quick-add-purchase-label">Quantity</span>
              <span class="quick-add-stock" data-stock-status></span>
            </div>
            <div class="quick-add-purchase-controls">
              <div class="quick-add-quantity" data-quantity-control>
                <button type="button" data-quantity-minus aria-label="Decrease quantity">−</button>
                <input type="number" inputmode="numeric" min="1" max="99" value="1" data-quantity-input aria-label="Quantity">
                <button type="button" data-quantity-plus aria-label="Increase quantity">+</button>
              </div>
              <button class="quick-add-modal-submit" type="button" data-add-to-cart>Add to Cart</button>
              <button class="quick-add-buy-now" type="button" data-buy-now>Buy Now</button>
            </div>
            <div class="quick-add-action-feedback" data-action-feedback aria-live="polite"></div>
          </div>

          <a class="quick-add-modal-alldetails" href="${esc(productUrl)}">View full product details <span aria-hidden="true">→</span></a>
        </section>
      </div>
    </div>`;

    updateVariantUi(false);
    goToImage(activeImageIndex, false);
    bindMobileGalleryScroll();
  }

  function updateVariantUi(moveImage = true) {
    currentVariant = findVariant();

    qsa('.quick-add-option-group', modal).forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const label = qs('[data-selected-option-label]', group);
      if (label) label.textContent = selectedOptions[index] || '';
      qsa('[data-option-value]', group).forEach((button) => {
        const selected = button.dataset.optionValue === selectedOptions[index];
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
    });

    const currentPrice = qs('[data-price-current]', modal);
    const comparePrice = qs('[data-price-compare]', modal);
    const discount = qs('[data-price-discount]', modal);
    const prepaidPrice = qs('[data-prepaid-price]', modal);
    const prepaidSave = qs('[data-prepaid-save]', modal);
    const upiPrice = qs('[data-upi-price]', modal);
    const upiSave = qs('[data-upi-save]', modal);
    const stock = qs('[data-stock-status]', modal);
    const addButton = qs('[data-add-to-cart]', modal);
    const buyButton = qs('[data-buy-now]', modal);

    if (!currentVariant) {
      if (currentPrice) currentPrice.textContent = 'Unavailable';
      if (comparePrice) comparePrice.hidden = true;
      if (discount) discount.hidden = true;
      if (prepaidPrice) prepaidPrice.textContent = '—';
      if (prepaidSave) prepaidSave.textContent = '';
      if (upiPrice) upiPrice.textContent = '—';
      if (upiSave) upiSave.textContent = '';
      if (stock) stock.textContent = 'Variant unavailable';
      if (addButton) { addButton.disabled = true; addButton.textContent = 'Unavailable'; }
      if (buyButton) buyButton.disabled = true;
      return;
    }

    const sellingCents = Number(currentVariant.price || 0);
    const compareCents = Number(currentVariant.compare_at_price || 0);
    const sellingRupees = sellingCents / 100;
    const prepaidRupees = Math.round(sellingRupees * 0.9);
    const upiRupees = Math.max(0, prepaidRupees - 30);
    const prepaidSavings = Math.max(0, Math.round(sellingRupees - prepaidRupees));
    const upiSavings = Math.max(0, Math.round(sellingRupees - upiRupees));

    if (currentPrice) currentPrice.textContent = formatMoney(sellingCents);
    if (comparePrice) {
      if (compareCents > sellingCents) {
        comparePrice.textContent = `MRP ${formatMoney(compareCents)}`;
        comparePrice.hidden = false;
      } else {
        comparePrice.hidden = true;
      }
    }
    if (discount) {
      if (compareCents > sellingCents && compareCents > 0) {
        discount.textContent = `${Math.round(((compareCents - sellingCents) / compareCents) * 100)}% OFF`;
        discount.hidden = false;
      } else {
        discount.hidden = true;
      }
    }

    if (prepaidPrice) prepaidPrice.textContent = formatRupees(prepaidRupees);
    if (prepaidSave) prepaidSave.textContent = prepaidSavings > 0 ? `Save ${formatRupees(prepaidSavings)}` : '';
    if (upiPrice) upiPrice.textContent = formatRupees(upiRupees);
    if (upiSave) upiSave.textContent = upiSavings > 0 ? `Save ${formatRupees(upiSavings)}` : '';

    const available = Boolean(currentVariant.available);
    if (stock) {
      stock.textContent = available ? 'In stock' : 'Sold out';
      stock.classList.toggle('is-sold-out', !available);
    }
    if (addButton) {
      addButton.disabled = !available;
      addButton.textContent = available ? 'Add to Cart' : 'Sold out';
    }
    if (buyButton) buyButton.disabled = !available;

    if (moveImage && currentVariant.featured_image) {
      const slides = qsa('.quick-add-media-slide img', modal);
      const wanted = normalizeUrl(currentVariant.featured_image);
      const index = slides.findIndex((img) => normalizeUrl(img.currentSrc || img.src) === wanted);
      if (index >= 0) goToImage(index, true);
    }
  }

  function goToImage(index, smooth = true) {
    const track = qs('.quick-add-media-track', modal);
    const slides = qsa('.quick-add-media-slide', modal);
    if (!track || !slides.length) return;

    const safeIndex = Math.max(0, Math.min(Number(index) || 0, slides.length - 1));
    activeImageIndex = safeIndex;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile) {
      const target = slides[safeIndex];
      track.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      track.style.transform = `translate3d(-${safeIndex * 100}%,0,0)`;
    }

    qsa('.quick-add-modal-thumb', modal).forEach((thumb) => {
      const active = Number(thumb.dataset.imageIndex) === safeIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
    const current = qs('[data-image-current]', modal);
    if (current) current.textContent = safeIndex + 1;
  }

  function bindMobileGalleryScroll() {
    const track = qs('.quick-add-media-track', modal);
    if (!track) return;
    track.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const slides = qsa('.quick-add-media-slide', modal);
        if (!slides.length) return;
        let nearest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const d = Math.abs(slide.offsetLeft - track.scrollLeft);
          if (d < distance) { distance = d; nearest = index; }
        });
        activeImageIndex = nearest;
        const current = qs('[data-image-current]', modal);
        if (current) current.textContent = nearest + 1;
      }, 70);
    }, { passive: true });
  }

  function setBusy(isBusy, action) {
    qsa('[data-add-to-cart],[data-buy-now]', modal).forEach((button) => {
      button.classList.toggle('is-busy', isBusy);
      button.disabled = isBusy || !currentVariant?.available;
    });
    const button = action === 'buy' ? qs('[data-buy-now]', modal) : qs('[data-add-to-cart]', modal);
    if (button) button.textContent = isBusy ? (action === 'buy' ? 'Opening checkout…' : 'Adding…') : (action === 'buy' ? 'Buy Now' : (currentVariant?.available ? 'Add to Cart' : 'Sold out'));
  }

  async function addSelectedToCart(autoOpenDrawer) {
    if (!currentVariant?.id || !currentVariant.available) throw new Error('Please select an available variant.');
    if (window.CartDrawer?.addToCart) {
      await window.CartDrawer.addToCart(currentVariant.id, quantity, { autoOpenDrawer });
      return;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: currentVariant.id, quantity }] })
    });
    if (!response.ok) {
      let message = 'Unable to add this item to cart.';
      try {
        const error = await response.json();
        message = error?.description || error?.message || message;
      } catch (_) {}
      throw new Error(message);
    }
  }

  async function handleAddToCart() {
    const feedback = qs('[data-action-feedback]', modal);
    if (feedback) feedback.textContent = '';
    setBusy(true, 'add');
    try {
      await addSelectedToCart(true);
      close();
    } catch (error) {
      if (feedback) feedback.textContent = error?.message || 'Unable to add this item to cart.';
      setBusy(false, 'add');
    }
  }

  async function handleBuyNow() {
    const feedback = qs('[data-action-feedback]', modal);
    if (feedback) feedback.textContent = '';
    setBusy(true, 'buy');
    try {
      await addSelectedToCart(false);
      window.location.assign('/checkout');
    } catch (error) {
      if (feedback) feedback.textContent = error?.message || 'Unable to continue to checkout.';
      setBusy(false, 'buy');
    }
  }

  function onModalClick(event) {
    const optionButton = event.target.closest('[data-option-value]');
    if (optionButton) {
      const optionIndex = Number(optionButton.dataset.optionIndex);
      selectedOptions[optionIndex] = optionButton.dataset.optionValue;
      updateVariantUi(true);
      return;
    }

    const thumb = event.target.closest('.quick-add-modal-thumb');
    if (thumb) {
      goToImage(Number(thumb.dataset.imageIndex), true);
      return;
    }

    if (event.target.closest('[data-gallery-prev]')) {
      const total = qsa('.quick-add-media-slide', modal).length;
      goToImage((activeImageIndex - 1 + total) % total, true);
      return;
    }
    if (event.target.closest('[data-gallery-next]')) {
      const total = qsa('.quick-add-media-slide', modal).length;
      goToImage((activeImageIndex + 1) % total, true);
      return;
    }

    if (event.target.closest('[data-quantity-minus]')) {
      quantity = Math.max(1, quantity - 1);
      const input = qs('[data-quantity-input]', modal);
      if (input) input.value = quantity;
      return;
    }
    if (event.target.closest('[data-quantity-plus]')) {
      quantity = Math.min(99, quantity + 1);
      const input = qs('[data-quantity-input]', modal);
      if (input) input.value = quantity;
      return;
    }

    if (event.target.closest('[data-add-to-cart]')) {
      handleAddToCart();
      return;
    }
    if (event.target.closest('[data-buy-now]')) handleBuyNow();
  }

  function onModalInput(event) {
    if (!event.target.matches('[data-quantity-input]')) return;
    const next = Math.max(1, Math.min(99, parseInt(event.target.value, 10) || 1));
    quantity = next;
    event.target.value = next;
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = previousBodyOverflow;
    const focusTarget = lastTrigger;
    lastTrigger = null;
    window.setTimeout(() => focusTarget?.focus?.({ preventScroll: true }), 20);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quick-add-btn]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    open(button.dataset.productHandle, button.dataset.productUrl, button);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && overlay?.classList.contains('is-open')) close();
  });

  window.addEventListener('resize', () => {
    if (!overlay?.classList.contains('is-open')) return;
    goToImage(activeImageIndex, false);
  }, { passive: true });
})();
